import { createFragmentContainer, graphql } from "react-relay";
import AgentBadge from "~/components/AgentBadge";
import { AttributeTable, AttributeTableRow } from "~/components/shared/AttributeTable";
import { type User, UserAvatarRelay as UserAvatar } from "~/components/shared/UserAvatar";
import JobEvent from "~/components/JobActivityTimeline/JobEvent";

type Props = {
  event: any;
  previousTimestamp: string | null | undefined;
};

type Actor = {
  type?: string | null;
  node?: unknown | null;
};

function ActorLabel({ actor }: { actor: Actor | null | undefined }) {
  switch (actor?.type) {
    case "AGENT":
      return actor.node ? <AgentBadge agent={actor.node} /> : <>Agent</>;
    case "USER": {
      const user = actor.node as User | null | undefined;

      return user ? (
        <>
          <UserAvatar className="mr-1" size="MICRO" user={user} suppressAltText={true} />
          {user.name}
        </>
      ) : (
        <>User</>
      );
    }
    case "DISPATCH":
      return <>Dispatcher</>;
    case "SYSTEM":
      return <>System</>;
    case "STACK":
      return <>Stack</>;
    default:
      return <>Actor</>;
  }
}

function JobEventPromisedExitStatus({ event, previousTimestamp }: Props) {
  const { actor, timestamp, promisedExitStatus, promisedExitStatusReason } = event;

  return (
    <JobEvent timestamp={timestamp} previousTimestamp={previousTimestamp}>
      <JobEvent.Label>
        <ActorLabel actor={actor} /> Declared Early Failure
      </JobEvent.Label>
      <JobEvent.Content>
        <AttributeTable>
          <AttributeTableRow title="Promised exit status">
            <code className="dark-gray monospace">
              <strong>{promisedExitStatus}</strong>
            </code>
          </AttributeTableRow>
          {promisedExitStatusReason && (
            <AttributeTableRow title="Reason">
              <span className="ph-no-capture whitespace-pre-wrap">{promisedExitStatusReason}</span>
            </AttributeTableRow>
          )}
        </AttributeTable>
      </JobEvent.Content>
    </JobEvent>
  );
}

export default createFragmentContainer(JobEventPromisedExitStatus, {
  event: graphql`
    fragment JobEventPromisedExitStatus_event on JobEvent {
      ... on JobEvent {
        timestamp
        actor {
          type
          node {
            ...AgentBadge_agent
            ... on User {
              name
              uuid
              ...UserAvatar_user
            }
          }
        }

        ... on JobEventPromisedExitStatus {
          promisedExitStatus
          promisedExitStatusReason
        }
      }
    }
  `,
});
